import type { PaginatedResponse } from 'src/lib/axios';
import type { NewUserPayload, InviteUserPayload } from './schemas';
import type {
  AdminManagementUser,
  AdminManagementUserInvitation,
} from 'src/types/admin/user-management';

import useSWR from 'swr';
import { useMemo } from 'react';

import { usePaginatedMutation } from 'src/lib/swr';
import axiosInstance, { endpoints, fetcherWithHeaders } from 'src/lib/axios';

export const userCacheKeys = {
  users: (mainGroupId: number) => endpoints.admin.management.users(mainGroupId),
  invitations: (mainGroupId: number) => endpoints.admin.management.invitations(mainGroupId),
};

type UseGetUsersParams = {
  mainGroupId?: number;
  page?: number;
  size?: number;
  sort?: string[];
  search?: string;
  groups?: number[];
  group?: number | null;
  adminRoleId?: number | null;
};

export function useGetUsers({
  mainGroupId,
  page = 0,
  size = 20,
  sort = ['id,desc'],
  search,
  groups,
  group,
  adminRoleId,
}: UseGetUsersParams = {}) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('size', size.toString());
  sort.forEach((s: string) => params.append('sort', s));
  if (search?.trim()) {
    params.append('search', search.trim());
  }
  if (groups && groups.length > 0) {
    groups.forEach((groupId: number) => params.append('groups', groupId.toString()));
  }
  if (group !== null && group !== undefined) {
    params.append('group', group.toString());
  }
  if (adminRoleId !== null && adminRoleId !== undefined) {
    params.append('adminRoleId', adminRoleId.toString());
  }
  const url = mainGroupId
    ? `${endpoints.admin.management.users(mainGroupId)}?${params.toString()}`
    : null;
  const {
    data: response,
    isLoading,
    error,
    isValidating,
  } = useSWR<PaginatedResponse<AdminManagementUser[]>>(url, fetcherWithHeaders);

  return useMemo(
    () => ({
      users: response?.data ?? [],
      totalCount: response?.totalCount ?? 0,
      usersLoading: isLoading,
      usersError: error,
      usersValidating: isValidating,
      usersEmpty: !isLoading && (response?.data?.length ?? 0) === 0,
    }),
    [response, error, isLoading, isValidating]
  );
}

type UseGetInvitationsParams = {
  mainGroupId?: number;
  page?: number;
  size?: number;
  sort?: string[];
  search?: string;
  groups?: number[];
  group?: number | null;
  adminRoleId?: number | null;
};

export function useGetInvitations({
  mainGroupId,
  page = 0,
  size = 20,
  sort = ['id,desc'],
  search,
}: UseGetInvitationsParams = {}) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('size', size.toString());
  sort.forEach((s: string) => params.append('sort', s));
  if (search?.trim()) {
    params.append('search', search.trim());
  }
  const url = mainGroupId
    ? `${endpoints.admin.management.invitations(mainGroupId)}?${params.toString()}`
    : null;
  const {
    data: response,
    isLoading,
    error,
    isValidating,
  } = useSWR<PaginatedResponse<AdminManagementUserInvitation[]>>(url, fetcherWithHeaders);

  return useMemo(
    () => ({
      invitations: response?.data ?? [],
      totalCount: response?.totalCount ?? 0,
      invitationsLoading: isLoading,
      invitationsError: error,
      invitationsValidating: isValidating,
      invitationsEmpty: !isLoading && (response?.data?.length ?? 0) === 0,
    }),
    [response, error, isLoading, isValidating]
  );
}

export function useCreateUser(mainGroupId: number) {
  return usePaginatedMutation<NewUserPayload>(
    userCacheKeys.users(mainGroupId),
    async (payload: NewUserPayload): Promise<void> => {
      await axiosInstance.post(userCacheKeys.users(mainGroupId), payload);
    }
  );
}

export function useRemoveUser(mainGroupId: number) {
  return usePaginatedMutation<number>(
    userCacheKeys.users(mainGroupId),
    async (userId: number): Promise<void> => {
      await axiosInstance.delete(endpoints.admin.management.removeUser(mainGroupId, userId));
    }
  );
}

export function useInviteUser(mainGroupId: number) {
  return usePaginatedMutation<InviteUserPayload>(
    userCacheKeys.invitations(mainGroupId),
    async (payload: InviteUserPayload): Promise<void> => {
      await axiosInstance.post(userCacheKeys.invitations(mainGroupId), payload);
    }
  );
}

export function useSetUserPassword(mainGroupId: number) {
  return usePaginatedMutation<{ userId: number; newPassword: string }>(
    userCacheKeys.users(mainGroupId),
    async ({ userId, newPassword }): Promise<void> => {
      await axiosInstance.post(endpoints.admin.management.setUserPassword(mainGroupId, userId), {
        newPassword,
      });
    }
  );
}

export function useRemoveInvitation(mainGroupId: number) {
  return usePaginatedMutation<number>(
    userCacheKeys.invitations(mainGroupId),
    async (invitationId: number): Promise<void> => {
      await axiosInstance.delete(
        endpoints.admin.management.removeInvitation(mainGroupId, invitationId)
      );
    }
  );
}
